import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { RichTextEditor, Spinner } from '../../../shared/components';
import {
  EmailSnippet,
  EmailSnippetInput,
  createEmailSnippet,
  deleteEmailSnippet,
  listEmailSnippets,
  updateEmailSnippet,
} from '../services/emailSnippetService';
import { Field } from './emailFormParts';
import styles from './EmailConnectorTab.module.css';
import brandStyles from './EmailBrandingPanel.module.css';
import layoutStyles from './EmailLayoutsEditor.module.css';

interface Draft extends EmailSnippetInput {
  id: string | null;
}

const EMPTY_DRAFT: Draft = { id: null, name: '', shortcut: '', category: '', bodyHtml: '' };

const draftFromSnippet = (snippet: EmailSnippet): Draft => ({
  id: snippet.id,
  name: snippet.name,
  shortcut: snippet.shortcut ?? '',
  category: snippet.category ?? '',
  bodyHtml: snippet.bodyHtml,
});

interface EmailSnippetsEditorProps {
  canManage: boolean;
}

export const EmailSnippetsEditor: React.FC<EmailSnippetsEditorProps> = ({ canManage }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();

  const [snippets, setSnippets] = React.useState<EmailSnippet[]>([]);
  const [variables, setVariables] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [baseline, setBaseline] = React.useState<Draft | null>(null);
  const [search, setSearch] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [insertRequest, setInsertRequest] = React.useState<{ id: number; html: string } | null>(null);

  const open = React.useCallback((next: Draft) => {
    setDraft(next);
    setBaseline(next);
    setInsertRequest(null);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await listEmailSnippets();
        if (cancelled) {
          return;
        }
        setSnippets(result.snippets);
        setVariables(result.variables);
        if (result.snippets.length > 0) {
          open(draftFromSnippet(result.snippets[0]));
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : t('emailSnippetLoadFailed'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, t]);

  const isDirty = Boolean(draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline));
  const disabled = !canManage || busy;

  const filtered = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return snippets;
    }
    return snippets.filter(snippet =>
      snippet.name.toLowerCase().includes(query)
      || snippet.shortcut?.includes(query)
      || snippet.category?.toLowerCase().includes(query));
  }, [search, snippets]);

  const confirmDiscard = () => !isDirty || window.confirm(t('emailSnippetDiscardConfirm'));

  const save = async () => {
    if (!draft) {
      return;
    }
    setBusy(true);
    try {
      const input: EmailSnippetInput = {
        name: draft.name.trim(),
        shortcut: draft.shortcut.trim(),
        category: draft.category.trim(),
        bodyHtml: draft.bodyHtml,
      };
      const saved = draft.id ? await updateEmailSnippet(draft.id, input) : await createEmailSnippet(input);
      setSnippets(prev => {
        const rest = prev.filter(snippet => snippet.id !== saved.id);
        return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      open(draftFromSnippet(saved));
      showToast(t('emailSnippetSaved'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('emailSnippetSaveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!draft?.id || !window.confirm(t('emailSnippetDeleteConfirm', { name: draft.name }))) {
      return;
    }
    setBusy(true);
    try {
      await deleteEmailSnippet(draft.id);
      const remaining = snippets.filter(snippet => snippet.id !== draft.id);
      setSnippets(remaining);
      if (remaining.length > 0) {
        open(draftFromSnippet(remaining[0]));
      } else {
        setDraft(null);
        setBaseline(null);
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('emailSnippetSaveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (loadError) {
    return <div className="error">{loadError}</div>;
  }

  return (
    <div className={layoutStyles.root}>
      <aside className={layoutStyles.sidebar}>
        {snippets.length > 0 ? (
          <input
            className="text-input"
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('emailSnippetSearch')}
            aria-label={t('emailSnippetSearch')}
          />
        ) : null}
        <ul className={layoutStyles.list} aria-label={t('emailSnippetsHeading')}>
          {filtered.map(snippet => (
            <li key={snippet.id}>
              <button
                type="button"
                className={`${layoutStyles.listItem} ${draft?.id === snippet.id ? layoutStyles.listItemOn : ''}`}
                onClick={() => { if (confirmDiscard()) open(draftFromSnippet(snippet)); }}
                disabled={busy}
                aria-current={draft?.id === snippet.id}
              >
                <span className={layoutStyles.listName}>{snippet.name}</span>
                {snippet.shortcut ? <span className={styles.muted}>/{snippet.shortcut}</span> : null}
              </button>
            </li>
          ))}
        </ul>
        {snippets.length === 0 ? <p className={styles.muted}>{t('emailSnippetNone')}</p> : null}
        {canManage ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => { if (confirmDiscard()) open({ ...EMPTY_DRAFT }); }}
            disabled={busy}
          >
            {t('emailSnippetCreate')}
          </button>
        ) : null}
      </aside>

      {draft ? (
        <div className={brandStyles.editor}>
          <div className="company-form-grid">
            <Field label={t('emailSnippetName')}>
              <input className="text-input" value={draft.name} onChange={event => setDraft({ ...draft, name: event.target.value })} disabled={disabled} maxLength={120} />
            </Field>
            <Field label={t('emailSnippetShortcut')} hint={t('emailSnippetShortcutHint')}>
              <input className="text-input" value={draft.shortcut} onChange={event => setDraft({ ...draft, shortcut: event.target.value })} disabled={disabled} maxLength={41} placeholder="thanks" />
            </Field>
            <Field label={t('emailSnippetCategory')}>
              <input className="text-input" value={draft.category} onChange={event => setDraft({ ...draft, category: event.target.value })} disabled={disabled} maxLength={60} />
            </Field>
          </div>

          <h4 className={styles.subHeading}>{t('emailSnippetBody')}</h4>
          {canManage ? (
            <RichTextEditor
              key={draft.id ?? 'new'}
              value={draft.bodyHtml}
              onChange={html => setDraft(prev => (prev ? { ...prev, bodyHtml: html } : prev))}
              minHeight={160}
              ariaLabel={t('emailSnippetBody')}
              insertRequest={insertRequest}
            />
          ) : (
            // eslint-disable-next-line react/no-danger
            <div className={styles.muted} dangerouslySetInnerHTML={{ __html: draft.bodyHtml }} />
          )}
          {canManage && variables.length > 0 ? (
            <div className={layoutStyles.layoutActions}>
              <span className={styles.small}>{t('emailSnippetVariables')}</span>
              {variables.map(variable => (
                <button
                  key={variable}
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setInsertRequest(prev => ({ id: (prev?.id ?? 0) + 1, html: `{{${variable}}}` }))}
                  disabled={disabled}
                >
                  {`{{${variable}}}`}
                </button>
              ))}
            </div>
          ) : null}

          {canManage ? (
            <div className={brandStyles.actions}>
              <span className={`${styles.footerNote} ${isDirty ? styles.footerDirty : ''}`}>
                {isDirty ? t('emailUnsavedChanges') : t('emailAllSaved')}
              </span>
              {draft.id ? (
                <button type="button" className="btn btn-secondary" onClick={() => { void remove(); }} disabled={busy}>
                  {t('emailSnippetDelete')}
                </button>
              ) : null}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => { void save(); }}
                disabled={busy || !isDirty || !draft.name.trim() || !draft.bodyHtml.trim()}
              >
                {busy ? t('savingEllipsis', { ns: 'admin' }) : t('emailSnippetSave')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
